import { useCallback, useState } from 'react'

import {
  faCopy,
  faThumbsDown as faThumbsDownRegular,
  faThumbsUp as faThumbsUpRegular,
} from '@fortawesome/pro-regular-svg-icons'
import {
  faThumbsDown as faThumbsDownSolid,
  faThumbsUp as faThumbsUpSolid,
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clipboardy from 'clipboardy'
import { Icon, T, Tooltip } from 'govwell-ui'
import { HStack } from 'govwell-ui'
import { htmlToText } from 'html-to-text'

import { useUpdateAIChatMutation } from 'src/fetch/aiChat'
import useDisclosure from 'src/hooks/use-disclosure'

type Props = {
  html: string
  id?: number
}

const Toolbar = ({ html, id }: Props) => {
  const { isOpen: isAnimating, open: startAnimating, close: stopAnimating } = useDisclosure()
  const handleCopy = useCallback(async () => {
    startAnimating()
    try {
      await clipboardy.write(htmlToText(html))

      setTimeout(() => {
        stopAnimating()
      }, 1000)
    } catch {
      stopAnimating()
    }
  }, [html, startAnimating, stopAnimating])

  const [userGaveThumbsUp, setUserGaveThumbsUp] = useState(false)
  const [userGaveThumbsDown, setUserGaveThumbsDown] = useState(false)

  const { mutateAsync: updateAIChat } = useUpdateAIChatMutation()

  const handleThumbsUp = useCallback(async () => {
    if (id && !userGaveThumbsUp) {
      setUserGaveThumbsUp(true)
      setUserGaveThumbsDown(false)
      await updateAIChat({
        id,
        input: {
          userGaveThumbsUp: true,
          userGaveThumbsDown: false,
        },
      })
    }
  }, [id, updateAIChat, userGaveThumbsUp])
  const handleThumbsDown = useCallback(async () => {
    if (id && !userGaveThumbsDown) {
      setUserGaveThumbsDown(true)
      setUserGaveThumbsUp(false)
      await updateAIChat({
        id,
        input: {
          userGaveThumbsUp: false,
          userGaveThumbsDown: true,
        },
      })
    }
  }, [id, updateAIChat, userGaveThumbsDown])

  return (
    <HStack gap={2} align="center" className="ml-3">
      <Tooltip content={isAnimating ? 'Copied' : 'Copy to clipboard'}>
        <div>
          {isAnimating ? (
            <T.Body className="text-green-700">
              <Icon.FaCheck />
            </T.Body>
          ) : (
            <FontAwesomeIcon
              icon={faCopy}
              style={{ cursor: 'pointer', width: '14px', height: '14px' }}
              onClick={handleCopy}
            />
          )}
        </div>
      </Tooltip>
      {id && (
        <>
          <Tooltip content="Helpful">
            <FontAwesomeIcon
              icon={userGaveThumbsUp ? faThumbsUpSolid : faThumbsUpRegular}
              style={{ cursor: 'pointer', width: '14px', height: '14px' }}
              onClick={handleThumbsUp}
            />
          </Tooltip>
          <Tooltip content="Not helpful">
            <FontAwesomeIcon
              icon={userGaveThumbsDown ? faThumbsDownSolid : faThumbsDownRegular}
              style={{ cursor: 'pointer', width: '14px', height: '14px' }}
              onClick={handleThumbsDown}
            />
          </Tooltip>
        </>
      )}
    </HStack>
  )
}

export default React.memo(Toolbar)
